import { parseMoneyInput } from "@erp/shared";
import { useState } from "react";
import { cn } from "~/lib/cn";
import { useFormat, useLocale } from "~/lib/i18n";
import { Input } from "./input";

interface MoneyInputProps {
  /** Integer cents, or null when empty. */
  value: number | null;
  onChange: (cents: number | null) => void;
  suffix?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}

/** Accepts "12,50", "12.5", "1 200" or, in English, "1,200.50"; shows a locale-formatted amount once the field loses focus. */
export function MoneyInput({ value, onChange, suffix, className, ...rest }: MoneyInputProps) {
  const format = useFormat();
  const [locale] = useLocale();
  const display = (cents: number | null) =>
    cents === null ? "" : format.number(cents / 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [draft, setDraft] = useState(() => display(value));
  const [focused, setFocused] = useState(false);
  const [synced, setSynced] = useState(value);
  // External value change (reset, another field) while not editing: show it.
  if (!focused && value !== synced) {
    setSynced(value);
    setDraft(display(value));
  }

  return (
    <div className="relative">
      <Input
        {...rest}
        inputMode="decimal"
        value={draft}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          // An unreadable entry stays visible (and invalid) instead of vanishing without a word.
          if (draft.trim() === "" || value !== null) setDraft(display(value));
        }}
        onChange={(e) => {
          const cents = e.target.value.trim() === "" ? null : parseMoneyInput(e.target.value, locale);
          setDraft(e.target.value);
          setSynced(cents);
          onChange(cents);
        }}
        aria-invalid={rest["aria-invalid"] || (draft.trim() !== "" && value === null) || undefined}
        className={cn("text-right tabular", suffix && "pr-12", className)}
      />
      {suffix ? (
        <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-fg-3">{suffix}</span>
      ) : null}
    </div>
  );
}
